"use client";

import { useState, useTransition } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import type { SerializedEditorState } from "lexical";
import dynamic from "next/dynamic";

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/ui/user-avatar";
import { ClanTagBadge } from "@/components/ui/clan-tag-badge";
import { deleteNewsComment } from "@/query/news";
import type { NewsComment } from "@/lib/types";
import { formatDateTimeShort } from "@/lib/format";

const Editor = dynamic(() => import("@/components/editor/editor").then((m) => m.Editor), { ssr: false });

function parseState(json: string): SerializedEditorState | undefined {
  try {
    return JSON.parse(json);
  } catch {
    return undefined;
  }
}

type Props = { comment: NewsComment; currentUserId: string | null; isAdmin?: boolean };

export function NewsCommentItem({ comment, currentUserId, isAdmin = false }: Props) {
  const [isPending, startTransition] = useTransition();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isOwner = !!currentUserId && comment.user_id === currentUserId;
  const canDelete = isOwner || isAdmin;

  function handleDelete() {
    startTransition(async () => {
      try {
        await deleteNewsComment(comment.comment_id, comment.news_id);
        setDeleteOpen(false);
        toast.success("Kommentar gelöscht.");
      } catch {
        toast.error("Fehler beim Löschen.");
      }
    });
  }

  const formatDate = (date: Date) => formatDateTimeShort(date);

  return (
    <div className="rounded-lg bg-card shadow shadow-card-shadow p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserAvatar name={comment.author_name} image={comment.author_image} className="h-14 w-14" />
          <div>
            <span className="font-semibold">{comment.author_name}</span>
            <ClanTagBadge tag={comment.author_clan_tag} className="ml-1.5" />
            <span className="text-xs text-muted-foreground ml-2">{formatDate(comment.created_at)}</span>
          </div>
        </div>
        {canDelete && (
          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDeleteOpen(true)} disabled={isPending}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <Editor readOnly editorSerializedState={parseState(comment.content)} />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kommentar löschen?</AlertDialogTitle>
            <AlertDialogDescription>{isOwner ? "Dein Kommentar wird unwiderruflich gelöscht." : `Der Kommentar von ${comment.author_name} wird unwiderruflich gelöscht.`}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
